// src/features/hotels/types/index.ts

// ============================================
// ENUMS
// ============================================
export enum OperationMode {
  /**
   * Solo acepta tags proporcionados manualmente (NFC, QR, códigos)
   */
  TAG_ONLY = 1,
  
  /**
   * Permite solicitudes automáticas por email + acepta tags
   */
  EMAIL_BASED = 2
}

/**
 * Tipos e interfaces para el feature de Hotels
 * Siguiendo el mismo patrón que Groups
 */
// ============================================
// MODELOS PRINCIPALES
// ============================================

/**
 * Modelo principal de Hotel
 */
export interface Hotel {
  id: string;
  name: string;
  address?: string;
  groupId?: string;
  groupName?: string;           // Nombre del grupo para mostrar
  group?: {                     // Objeto grupo anidado de la API
    id: string;
    name: string;
  };
  installationCode?: string;     // Código de instalación único
  defaultLanguage?: string;      // Idioma por defecto
  contactEmail?: string;         // Email de contacto
  contactPhone?: string;         // Teléfono de contacto
  
  // Contadores
  hotelCount?: number;          // Número de habitaciones (legacy)
  userCount?: number;           // Número de usuarios asociados
  lockerCount?: number;         // Número de taquillas
  hmiCount?: number;            // Número de HMIs
  controllerCount?: number;     // Número de controladores
  
  isActive: boolean;            // Estado activo/inactivo
  zones?: Zone[];               // Zonas del hotel
  settings?: HotelSettings;     // Configuración del hotel
  
  // Propiedades adicionales para las tarjetas de HotelEdit
  supportedLanguages?: string[];              // Idiomas soportados
  notifications?: NotificationSettings;       // Configuración de notificaciones
  referenceFields?: ReferenceField[];        // Campos de referencia
  supportedTagTypes?: TagType[];             // Tipos de tags soportados
  customTexts?: CustomTexts;                 // Textos personalizados multiidioma
  
  createdAt: string;            // Fecha de creación ISO
  updatedAt: string;            // Fecha de actualización ISO

  operationMode?: OperationMode;
  emailAssignmentDuration?: number;
  codeExpirationMinutes?: number;
  lockerHoldMinutes?: number;
  supportedTagTypeIds?: string[];
}



export interface NotifierConfiguration {
    notifierApiKey?: string;
    accountName?: string;
    selectedTemplateId?: string;
    defaultLanguage?: string;
    timeZone?: string;
    isConfigured: boolean;
}


export interface NotifierTemplate {
    id: string;
    name: string;
    type: string;
    language: string;
    applications?: string[];
    availableLanguages?: string[];
}



/**
 * Zona del hotel
 */
export interface Zone {
  id: string;
  name: string;
  description?: string;
  order: number;
  isActive: boolean;
  lockerCount?: number;
  parentZoneId?: string;        // Para sub-zonas
  subZones?: Zone[];            // Sub-zonas
}

/**
 * Configuración del hotel
 */
export interface HotelSettings {
  reservationDuration?: number;      // Duración por defecto de reservas (minutos)
  gracePeriod?: number;              // Período de gracia (minutos)
  maxReservationsPerUser?: number;   // Máximo de reservas por usuario
  autoReleaseEnabled?: boolean;      // Liberación automática
  maintenanceMode?: boolean;         // Modo mantenimiento
  allowGuestReservations?: boolean;  // Permitir reservas de invitados
  requireEmail?: boolean;            // Email obligatorio
  requirePhone?: boolean;            // Teléfono obligatorio
  notificationSettings?: NotificationSettings;
}

export interface HotelConfigurationResponse {
  id: string;
  name: string;
  installationCode?: string;
  
  // UI Configuration
  primaryColor: string;
  defaultLanguage: string;
  logoBase64?: string;
  availableLanguages: string[];
  
  // Business Configuration
  operationMode: OperationMode;
  emailAssignmentDuration: number;
  codeExpirationMinutes: number;
  lockerHoldMinutes: number;
  
  // Supported Tag Types
  supportedTagTypes: Array<{
    id: string;
    name: string;
    typeCode: string;
  }>;
  
  // PMS Integration
  pmsIntegration?: {
    enabled: boolean;
    webhookApiKey?: string;
  };
  
  // Security
  sharedSecret?: string;
}


/**
 * Configuración de notificaciones extendida
 */
export interface NotificationSettings {
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  reminderMinutes?: number;
  expirationWarning?: boolean;
  
  // Configuración detallada por tipo de evento
  lockerAssigned?: NotificationChannel;
  lockerUnassigned?: NotificationChannel;
  lockerOpened?: NotificationChannel;
  lockerClosed?: NotificationChannel;
  lockerMaintenance?: NotificationChannel;
  systemAlert?: NotificationChannel;
  dailyReport?: NotificationChannel;
  weeklyReport?: NotificationChannel;
}

/**
 * Canal de notificación
 */
export interface NotificationChannel {
  enabled: boolean;
  recipients?: string[];
  template?: string;
  conditions?: NotificationCondition[];
}

/**
 * Condición de notificación
 */
export interface NotificationCondition {
  field: string;
  operator: string;
  value: string | number;
}

/**
 * Campo de referencia para identificación
 */
export interface ReferenceField {
  id: string;
  name: string;
  type: 'tag' | 'card' | 'biometric' | 'qr' | 'pin';
  fieldKey: string;
  required: boolean;
  unique: boolean;
  validation?: string;
  order: number;
}

/**
 * Tipo de tag soportado
 */
export interface TagType {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

/**
 * Textos personalizados multiidioma
 */
export interface CustomTexts {
  [languageCode: string]: {
    [key: string]: string;
  };
}

// ============================================
// FORMULARIOS Y DATOS DE ENTRADA
// ============================================

/**
 * Datos del formulario de hotel
 */
export interface HotelFormData {
  name: string;
  address?: string;
  groupId?: string;
  installationCode?: string;
  defaultLanguage?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive?: boolean;
  settings?: Partial<HotelSettings>;
  operationMode?: OperationMode;
  emailAssignmentDuration?: number;
  codeExpirationMinutes?: number;
  lockerHoldMinutes?: number;
  supportedTagTypeIds?: string[];
}

/**
 * Filtros para la lista de hoteles
 */
export interface HotelFilters {
  search?: string;
  groupId?: string;
  isActive?: boolean;
  country?: string;
  hasZones?: boolean;
}

// ============================================
// ESTADÍSTICAS Y MÉTRICAS
// ============================================

/**
 * Estadísticas de hotel
 */
export interface HotelStats {
  totalZones: number;
  activeZones: number;
  
  totalLockers: number;
  availableLockers: number;
  occupiedLockers: number;
  maintenanceLockers: number;
  
  totalUsers: number;
  activeUsers: number;
  
  activeAssignments: number;
  
  occupancyRate: number;        // Porcentaje de ocupación
  averageOccupancyTime?: number; // Tiempo promedio de ocupación en minutos
  peakHours?: number[];         // Horas pico de uso
  
  notificationsEnabled: boolean;
  maintenanceMode: boolean;
}

// ============================================
// TIPOS DE RESPUESTA DE LA API
// ============================================

/**
 * Respuesta de la API para la lista de hoteles
 */
export interface HotelListResponse {
  data: Hotel[];
  total: number;
  page: number;
  perPage: number;
}

// ============================================
// PROPS DE COMPONENTES
// ============================================

export interface HotelListProps {
  hasCreate?: boolean;
  hasEdit?: boolean;
  hasShow?: boolean;
  perPage?: number;
  sort?: { field: string; order: string };
}

export interface HotelCreateProps {
  redirect?: string | false;
  transform?: (data: any) => any;
}

export interface HotelEditProps {
  id?: string;
  redirect?: string | false;
  transform?: (data: any) => any;
}

export interface HotelActionsProps {
  record: Hotel;
  small?: boolean;
}

export interface UpdateHotelSettingsRequest {
  // Información básica
  name?: string;
  address?: string;
  groupId?: string;
  installationCode?: string;
  defaultLanguage?: string;
  
  // Configuración de operación
  operationMode?: OperationMode;
  emailAssignmentDuration?: number;
  codeExpirationMinutes?: number;
  lockerHoldMinutes?: number;
  
  // Tag types
  supportedTagTypeIds?: string[];
}

export interface TagType {
  id: string;
  name: string;
  typeCode: string;
  description?: string;
  isSystemGenerated: boolean;
  requiresManualInput: boolean;
  isActive: boolean;
}


// ============================================
// TIPOS DE VALIDACIÓN
// ============================================

/**
 * Errores de validación del formulario de hotel
 */
export interface HotelValidationErrors {
  name?: string;
  address?: string;
  groupId?: string;
  installationCode?: string;
  defaultLanguage?: string;
  contactEmail?: string;
  contactPhone?: string;
}

// ============================================
// TIPOS DE HOOKS
// ============================================

/**
 * Resultado del hook useHotelData
 */
export interface UseHotelDataResult {
  hotels: Hotel[];
  loading: boolean;
  error: Error | null;
  
  // Operaciones CRUD
  fetchHotels: () => Promise<void>;
  fetchHotel: (id: string) => Promise<Hotel | null>;
  createHotel: (hotelData: Partial<Hotel>) => Promise<Hotel | null>;
  updateHotel: (id: string, hotelData: Partial<Hotel>) => Promise<Hotel | null>;
  deleteHotel: (id: string) => Promise<boolean>;
  
  // Funciones de utilidad
  getHotelsByGroup: (groupId: string) => Hotel[];
  getActiveHotels: () => Hotel[];
  searchHotels: (searchTerm: string) => Hotel[];
  
  // Estadísticas
  totalCount: number;
  activeCount: number;
  inactiveCount: number;
}

// ============================================
// FUNCIONES DE UTILIDAD
// ============================================

/**
 * Normaliza los datos de hotel de la API
 */
export const normalizeHotel = (apiHotel: any): Hotel => ({
  id: apiHotel.id,
  name: apiHotel.name || apiHotel.hotel_name || '',
  address: apiHotel.address || '',
  groupId: apiHotel.groupId || apiHotel.group_id,
  groupName: apiHotel.groupName || apiHotel.group_name || apiHotel.group?.name,
  group: apiHotel.group,
  installationCode: apiHotel.installationCode || apiHotel.installation_code,
  defaultLanguage: apiHotel.defaultLanguage || apiHotel.default_language || 'es',
  contactEmail: apiHotel.contactEmail || apiHotel.contact_email,
  contactPhone: apiHotel.contactPhone || apiHotel.contact_phone,
  isActive: apiHotel.isActive ?? apiHotel.is_active ?? true,
  hotelCount: apiHotel.hotelCount || 0,
  userCount: apiHotel.userCount || 0,
  lockerCount: apiHotel.lockerCount || 0,
  hmiCount: apiHotel.hmiCount || 0,
  controllerCount: apiHotel.controllerCount || 0,
  zones: apiHotel.zones || [],
  settings: apiHotel.settings || {},
  
  // Propiedades adicionales
  supportedLanguages: apiHotel.supportedLanguages || apiHotel.supported_languages || [apiHotel.defaultLanguage || 'es'],
  notifications: apiHotel.notifications || apiHotel.notification_settings || {},
  referenceFields: apiHotel.referenceFields || apiHotel.reference_fields || [],
  supportedTagTypes: apiHotel.supportedTagTypes || apiHotel.supported_tag_types || [],
  customTexts: apiHotel.customTexts || apiHotel.custom_texts || {},
  
  operationMode: apiHotel.operationMode || OperationMode.TAG_ONLY,
  emailAssignmentDuration: apiHotel.emailAssignmentDuration || 24,

  createdAt: apiHotel.createdAt || new Date().toISOString(),
  updatedAt: apiHotel.updatedAt || new Date().toISOString(),
});

/**
 * Prepara datos para enviar a la API
 */
export const prepareHotelForAPI = (hotel: HotelFormData): any => ({
  hotel_name: hotel.name,
  name: hotel.name, // Redundancia para compatibilidad
  address: hotel.address || '',
  groupId: hotel.groupId || null,
  installationCode: hotel.installationCode || '',
  defaultLanguage: hotel.defaultLanguage || 'es',
  contactEmail: hotel.contactEmail || '',
  contactPhone: hotel.contactPhone || '',
  isActive: hotel.isActive ?? true,
  settings: hotel.settings || {},
  operationMode: hotel.operationMode || OperationMode.TAG_ONLY,
  emailAssignmentDuration: hotel.emailAssignmentDuration || 24,
  codeExpirationMinutes: hotel.codeExpirationMinutes || 60,
  lockerHoldMinutes: hotel.lockerHoldMinutes || 15,
  supportedTagTypeIds: hotel.supportedTagTypeIds || [],
});



// ============================================
// CONSTANTES
// ============================================

/**
 * Idiomas soportados por los hoteles
 */
export const HOTEL_LANGUAGES = [
  { id: 'ES', name: 'Español' },
  { id: 'EN', name: 'English' },
  { id: 'FR', name: 'Français' },
  { id: 'DE', name: 'Deutsch' },
  { id: 'IT', name: 'Italiano' },
  { id: 'PT', name: 'Português' },
] as const;

/**
 * Estados de hoteles
 */
export const HOTEL_STATUSES = {
  ACTIVE: true,
  INACTIVE: false,
} as const;

/**
 * Tipos de notificaciones disponibles
 */
export const NOTIFICATION_TYPES = [
  'lockerAssigned',
  'lockerUnassigned',
  'lockerOpened',
  'lockerClosed',
  'lockerMaintenance',
  'systemAlert',
  'dailyReport',
  'weeklyReport',
] as const;

/**
 * Tipos de campos de referencia
 */
export const REFERENCE_FIELD_TYPES = [
  { id: 'tag', name: 'Tag RFID' },
  { id: 'card', name: 'Tarjeta' },
  { id: 'biometric', name: 'Biométrico' },
  { id: 'qr', name: 'Código QR' },
  { id: 'pin', name: 'PIN' },
] as const;

/**
 * Canales de notificación disponibles
 */
export const NOTIFICATION_CHANNELS = [
  { id: 'email', name: 'Email', icon: 'email' },
  { id: 'sms', name: 'SMS', icon: 'sms' },
  { id: 'push', name: 'Push', icon: 'notifications' },
] as const;

/**
 * Categorías de textos personalizados
 */
export const TEXT_CATEGORIES = [
  'welcome',
  'locker_operations', 
  'user_interaction',
  'errors_warnings',
] as const;